import { isObject } from '@/global/utils/utils'
import indexTableLeafletMapHelper from '../../../common/config/index-table-leaflet-map-helper'
import truncatedTextDisplay from '@/global/components/TruncatedTextDisplay.vue'
import { notify } from '@/global/services/notification/notificationBus'
import { dateService, FORMATS } from '@/global/services/date'
import { showDetailsAction } from '@/global/components/table/helpers/tableActions'
import { STATUS_PILL_CELL_CONFIG } from '../../../common/config/status-cell-helper'

export default {
  name: 'mainTableConfig',

  mixins: [
    indexTableLeafletMapHelper
  ],

  computed: {
    tableConfig () {
      return {
        tableConfig: {
          uiView: 'eRegulation_solutions_index',
          apiConfig: {
            get: {
              module: 'road-maintenance',
              route: 'traffic-regulation',
              queryParams: (event) => this.modifyFilters(event)
            }
          },
          showLoader: this.showLoader,
          // Opening the record is the double click, everywhere in the application.
          rowDblClick: (event) => this.showDetails(event),
          // A click on a row shows it on the map beside the table
          rowClickIndicator: 'map',
          rowsSelection: {
            // Picking rows is what tells the map what to show: their markers are
            // marked and the map is moved to hold all of them at once.
            selectionsChange: (event) => this.showSelectedMarkers(event)
          },
          onDataReceivedWithParams: (event) => {
            this.fetchMarkers(event).then()
          },
          sideTableComponent: {
            component: this.mainTableLeafletMapComponent,
            mapInitialized: async (event) => await this.mapInitialized(event),
            onAddedMarkers: () => {
              this.showLoader = false
            },
            queryParams: {
              type: 'RULING'
            }
          },
          globalStyle: {
            tableRowClasses: 'table-row',
            sideTableContentColClasses: 'table-side-col'
          },
          tableConfig: {
            cellsConfig: {
              regulation_setter: {
                custom_display_value: (event) => {
                  return event?.regulation_setter ? this.$t(event?.regulation_setter) : '-'
                }
              },
              source: {
                custom_display_value: (data) => {
                  return data?.source ? this.$t(data.source) : ''
                }
              },
              request_type: {
                custom_display_value: (data) => {
                  return data?.request_type?.description ? this.$t(data.request_type.description) : ''
                }
              },
              processing_entity: {
                custom_display_value: (data) => {
                  return data?.processing_entity ? this.$t(data.processing_entity) : ''
                }
              },
              status: {
                type: 'status',
                custom_display_value: (data) => {
                  if (data && data.status && isObject(data.status)) {
                    return {
                      ...data.status,
                      ...{
                        description: data.status.description ? this.$t(data.status.description) : '-'
                      }
                    }
                  }
                },
                displayValue: 'description',
                colorObjectKey: 'color',
                textColorKey: 'text',
                backgroundColorKey: 'background',
                ...STATUS_PILL_CELL_CONFIG
              }
            },
            headers: [
              {
                label: this.$t('road-maintenance/aggregator.request_id'),
                key: 'document_code',
                always_visible: true
              },
              {
                label: this.$t('road-maintenance/aggregator.request_created_date'),
                key: 'created_at',
                always_visible: true
              },
              {
                label: this.$t('road-maintenance/aggregator.class'),
                key: 'doc_class',
                initial_value: true
              },
              {
                label: this.$t('road-maintenance/aggregator.registration_number'),
                key: 'registry_number',
                initial_value: true
              },
              {
                label: this.$t('road-maintenance/dropdown-types.applicant'),
                key: 'applicant',
                component: truncatedTextDisplay,
                initial_value: true
              },
              {
                label: this.$t('road-maintenance/aggregator.request_address'),
                key: 'address',
                component: truncatedTextDisplay,
                initial_value: true
              },
              {
                label: this.$t('road-maintenance/aggregator.consent_valid_from_to'),
                key: 'consent_validity',
                initial_value: true
              },
              {
                label: this.$t('road-maintenance/aggregator.prp_sets'),
                key: 'regulation_setter',
                component: truncatedTextDisplay,
                initial_value: true
              },
              {
                label: this.$t('road-maintenance/aggregator.document_created_by'),
                key: 'created_by',
                component: truncatedTextDisplay,
                initial_value: true
              },
              {
                label: this.$t('fleet/vehicle.status'),
                key: 'status',
                style: {
                  width: '200px'
                },
                always_visible: true
              }
            ],
            paginationConfig: {
              perPageOptions: [20, 50, 100],
              perPage: 100,
              currentPage: 1
            },
            actionsConfig: [
              showDetailsAction(this.$t, (event) => this.showDetails(event))
            ],
            filtersConfig: {
              fields: [
                {
                  key: 'date_time_from',
                  label: this.$t('road-maintenance/aggregator.date_from'),
                  type: 'datetime-picker',
                  appendIcon: 'mdi-calendar-month',
                  format: FORMATS.datetimeShort,
                  placeholder: FORMATS.datetimeShort
                },
                {
                  key: 'date_time_to',
                  label: this.$t('road-maintenance/aggregator.date_to'),
                  type: 'datetime-picker',
                  appendIcon: 'mdi-calendar-month',
                  format: FORMATS.datetimeShort,
                  placeholder: FORMATS.datetimeShort
                },
                {
                  key: 'document_code',
                  label: this.$t('road-maintenance/aggregator.request_id')
                },
                {
                  key: 'doc_class',
                  label: this.$t('road-maintenance/aggregator.class')
                },
                {
                  key: 'registry_number',
                  label: this.$t('road-maintenance/aggregator.registration_number')
                },
                {
                  key: 'applicant',
                  label: this.$t('road-maintenance/dropdown-types.applicant')
                },
                {
                  key: 'regulation_setter_id',
                  label: this.$t('road-maintenance/aggregator.prp_sets'),
                  type: 'autocomplete',
                  clearable: true,
                  items: this.dropdownValues?.regulation_setter || [],
                  autocomplete_options: {
                    custom_display_value: (value) => {
                      const { description } = value || null
                      return description && typeof description === 'string' ? this.$t(description) : ''
                    },
                    form_value: 'id'
                  }
                },
                {
                  key: 'created_by',
                  label: this.$t('road-maintenance/aggregator.document_created_by')
                },
                {
                  key: 'status_id',
                  label: this.$t('fleet/vehicle.status'),
                  type: 'autocomplete',
                  clearable: true,
                  autocomplete_options: {
                    module: 'road-maintenance',
                    route: 'traffic-regulation/statuses',
                    allowSearch: false,
                    custom_display_value: (value) => {
                      const { description } = value || null
                      return description && typeof description === 'string' ? this.$t(description) : ''
                    },
                    form_value: 'id'
                  }
                }
              ]
            }
          }
        },
        breadcrumbs: {
          items: [
            {
              text: this.$t('road-maintenance.eRegulation')
            },
            {
              text: this.$t('road-maintenance.solutions'),
              bolded: true
            }
          ]
        }
      }
    }
  },

  methods: {
    showDetails (event) {
      const { data } = event || {}
      const { id, request_type: requestType, document_code: documentCode } = data || {}

      // skip if data is not valid
      if (!id || !requestType) {
        notify(this.$t('road-maintenance/aggregator.request_not_found'))
        return
      }

      const query = {
        category: 'request_overview',
        request_type: requestType?.name ?? '',
        from: 'Aggregator_solutions',
        document_code: documentCode
      }

      this.$router.push({
        name: 'Aggregator_solutions_details',
        params: {
          id
        },
        query
      }).then()
    },

    onMarkerClick (marker) {
      const { id } = marker || {}

      if (!id) {
        return
      }

      this.$router.replace({
        name: 'Aggregator_solutions_details',
        params: {
          id
        }
      })
    },

    modifyFilters (event) {
      const { filters } = event?.params || {}
      const newFilters = {
        filters: { ...filters },
        type: 'RULING'
      }

      // format date times
      if (filters?.date_time_from) {
        newFilters.filters.date_time_from = dateService.formatCustom(filters?.date_time_from, FORMATS.datetimeShort, FORMATS.isoDatetime)
      }
      if (filters?.date_time_to) {
        newFilters.filters.date_time_to = dateService.formatCustom(filters?.date_time_to, FORMATS.datetimeShort, FORMATS.isoDatetime)
      }

      return newFilters
    }
  }
}
